import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { preloadContentOwnershipCollaborators } from '@confluence/content-ownership/entry-points/preloadContentOwnershipCollaborators.ts';
import { preloadWebItemLocation } from '@confluence/web-item-location/entry-points/preloadWebItemLocation.ts';
import { preloadEditButton } from '@confluence/edit-button/entry-points/preloadEditButton.js';
import { preloadFabricEditorEligibility } from '@confluence/edit-button/entry-points/preloadFabricEditorEligibility.ts';
import { preloadReadTime } from '@confluence/read-time/entry-points/preloadReadTime.ts';
import { preloadContentAnalyticsViewers } from '@confluence/confluence-analytics/entry-points/preloadContentAnalyticsViewers.ts';
import { preloadPageRestrictionsQuery } from '@confluence/page-restrictions-context/entry-points/preloadPageRestrictionsQuery.ts';
import { preloadGrantAccessDialogData } from '@confluence/grant-access-dialog/entry-points/preloadGrantAccessDialogData.ts';
import { preloadEditorContentState } from '@confluence/content-state/entry-points/preloadEditorContentState.ts';
import { preloadRendererContentState } from '@confluence/content-state/entry-points/preloadRendererContentState.ts';
import { preloadCommentButton } from '@confluence/comment-button/entry-points/preloadCommentButton.ts';
import { preloadClassificationContextForViewContent } from '@confluence/data-classification/entry-points/queries.ts';
import type { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { expVal, expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import ContentHeaderUnifiedQuery from './__generated__/ContentHeaderUnifiedQuery.graphql.ts';
import { preloadAIPlannerSyncByline } from './by-line/ai-planner-sync/preloadAIPlannerSyncByline.ts';
import { DEFAULT_CONTRIBUTORS_FETCH_LIMIT, CONFLUENCE_EDITION } from './defaultQueryVariables.js';

export type preloadContentHeaderProps = {
	spaceKey?: string;
	contentId: string;
	username?: string;
	edition: ConfluenceEdition | null;
	isPrevEditRoute?: boolean;
	useNewContentTopper?: boolean;
	isLivePageRoute?: boolean;
	isBlog?: boolean;
	isContentTypePublicLinkEligible?: boolean;
	hasDocumentContractMetadata?: boolean;
	hasDocumentContractClassification?: boolean;
	hasDocumentContractBody?: boolean;
	hasDocumentContractAnalyticsByline?: boolean;
	hasDocumentContractContentHeader?: boolean;
	shouldPreloadPageMetadataBanner?: boolean;
	/** View Page route supplies fallback preloads for non-renderer responses. */
	deferRestrictions?: boolean;
};

export function preloadContentHeader({
	spaceKey,
	contentId,
	username,
	edition,
	isPrevEditRoute = false,
	useNewContentTopper = false,
	isLivePageRoute = false,
	isBlog = false,
	isContentTypePublicLinkEligible = false,
	hasDocumentContractMetadata = false,
	hasDocumentContractClassification = false,
	hasDocumentContractBody = false,
	hasDocumentContractAnalyticsByline = false,
	hasDocumentContractContentHeader = false,
	shouldPreloadPageMetadataBanner = true,
	deferRestrictions = false,
}: preloadContentHeaderProps): Promise<boolean> {
	const queries: Promise<any>[] = [];
	const { cloudId } = getPreloaderFnContext();
	if (cloudId && contentId && fg('ai-planner-confluence-byline-use-agg')) {
		queries.push(
			preloadAIPlannerSyncByline({
				cloudId,
				contentId,
				contentType: isBlog ? 'blogpost' : 'page',
			}),
		);
	}
	const shouldUseContentHeaderFragment =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		hasDocumentContractContentHeader &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_header_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false);
	const shouldUseFabricEligibilityFragment =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		shouldUseContentHeaderFragment &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_fabric_eligibility_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false);

	// The compatibility query is preloaded when the document-contract reader is unavailable.
	// On restricted pages we do not render or use its result.
	// Hence mute the restriction errors on preloading to avoid unnecessary noise.
	// The mute logic is done in muteExpectedGraphQLErrors, here we just want to know if errors happened.
	let hasErrors = false;
	const checkHasGraphQLErrors = (result: Awaited<ReturnType<typeof query>>) => {
		hasErrors ||= Boolean(result?.hasErrors);
		return result;
	};

	if (contentId && spaceKey && expValEquals('dual_agent_avatar_card', 'isEnabled', true)) {
		const { cloudId } = getPreloaderFnContext();
		if (cloudId) {
			queries.push(preloadContentOwnershipCollaborators({ cloudId, contentId, spaceKey }));
		}
	}

	// Don't preload queries that will be updated by editor if previous route is edit_page
	if (!isPrevEditRoute) {
		if (!shouldUseContentHeaderFragment) {
			queries.push(
				query({
					query: ContentHeaderUnifiedQuery,
					variables: {
						contentId,
						spaceKey,
						versionOverride: null,
						embeddedContentRender: null,
						limit: DEFAULT_CONTRIBUTORS_FETCH_LIMIT,
						useNewContentTopper,
						isMarkdownModeEnabled: expValEquals('cc-markdown-mode', 'isEnabled', true),
						...(expValEquals('confluence_classification_level_v2', 'isEnabled', true)
							? { useClassificationLevelV2: true }
							: {}),
						includeDevDocsBylineConnection: isExperimentEnabled(
							'confluence_1p_and_3p_connection_byline_experiment',
						),
						useObjectHeaderFragment:
							!StatsigConfigurations.isKillSwitchOn('confluence_vp_obj_header_frag_dedup') &&
							expVal('confluence_exp_vp_document', 'isEnabled', false),
						useEditButtonFragment:
							!StatsigConfigurations.isKillSwitchOn('confluence_vp_edit_frag_reader') &&
							expVal('confluence_exp_vp_document', 'isEnabled', false),
						useAccessButtonFragment:
							!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
							expVal('confluence_exp_vp_document', 'isEnabled', false),
						includePublicLink:
							isContentTypePublicLinkEligible &&
							!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
							expVal('confluence_exp_vp_document', 'isEnabled', false),
						includeClassification:
							hasDocumentContractClassification &&
							!StatsigConfigurations.isKillSwitchOn(
								'confluence_vp_classification_context_frag_reader',
							) &&
							expVal('confluence_exp_vp_document', 'isEnabled', false),
					},
					// eslint-disable-next-line check-react-ssr-usage/no-react-ssr -- This controls request-scoped cache retention, not rendered output.
					...(!process.env.REACT_SSR &&
					expVal('confluence_relay_vp_preload_retention', 'isEnabled', false)
						? {
								relayQueryPreloadRetention: 'environment',
							}
						: {}),
				}).then(checkHasGraphQLErrors),
			);
		}

		// Editor will preload the content state if it's a live page
		if (!isLivePageRoute) {
			const shouldUseRendererContentStateFragment =
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				hasDocumentContractMetadata &&
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
				expVal('confluence_exp_vp_document', 'isEnabled', false);

			if (!shouldUseRendererContentStateFragment) {
				queries.push(preloadRendererContentState(contentId).then(checkHasGraphQLErrors));
			}

			// The approvals experience still needs editor content state data.
			if (expValEquals('confluence_approvals_eap', 'isEnabled', true)) {
				queries.push(
					preloadEditorContentState({
						contentId,
						useCache: false,
						shouldGetDraftVersion: false,
					}).then(checkHasGraphQLErrors),
				);
			}
		}

		// SSR Grant Access Restrictions button
		// username is a legacy URL parameter that actually represents accountId in the grant access flow
		if (contentId && username) {
			queries.push(preloadGrantAccessDialogData(contentId, username));
		}

		//unlike SPA, no need to check the route since preloadContentHeader is fired only when its VIEW_PAGE.
		const includeAncestors = edition === CONFLUENCE_EDITION.FREE;
		// The route owns fallback preloading for live/draft/error responses when this is deferred.
		if (!deferRestrictions) {
			queries.push(preloadPageRestrictionsQuery(contentId, includeAncestors));
		}
	}

	const contentHeaderTimeout = StatsigConfigurations.getNumber(
		'confluence_frontend_preload_content_header_timeout',
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);
	const shouldUseAnalyticsBylineFragment =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		hasDocumentContractAnalyticsByline &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_analytics_byline_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false);
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		const shouldUseReadTimeFragment =
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			hasDocumentContractBody &&
			// Read time receives its fragment ref through ContentTitleWithByLine.
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			!StatsigConfigurations.isKillSwitchOn('confluence_vp_body_frag_reader') &&
			expVal('confluence_exp_vp_document', 'isEnabled', false);

		if (
			(StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') ||
				!expVal('confluence_exp_vp_document', 'isEnabled', false)) &&
			fg('confluence_frontend_usd_ssr')
		) {
			queries.push(preloadShareAndRestrictButtonQuery(contentId));
		}

		queries.push(
			...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_edit_frag_reader') &&
			expVal('confluence_exp_vp_document', 'isEnabled', false)
				? []
				: [preloadEditButton({ spaceKey, contentId })]),
			...(shouldUseAnalyticsBylineFragment
				? []
				: [
						slowQueryGuard(
							'preloadContentAnalyticsViewers',
							() => preloadContentAnalyticsViewers(contentId).then(checkHasGraphQLErrors),
							contentHeaderTimeout,
						),
					]),

			...(shouldUseReadTimeFragment
				? []
				: [
						preloadReadTime({
							contentId,
							// preloadContentHeader is called by preloadViewPageRoute and preloadLivePageRoute only at the time of this writing. preloadViewPageRoute fetches the content body already.
							includeContentBody: isLivePageRoute,
							useCache: false,
						}),
					]),
			preloadCommentButton(contentId),
		);

		if (
			!hasDocumentContractClassification ||
			StatsigConfigurations.isKillSwitchOn('confluence_vp_classification_context_frag_reader') ||
			!expVal('confluence_exp_vp_document', 'isEnabled', false)
		) {
			queries.push(preloadClassificationContextForViewContent(contentId));
		}

		if (!shouldUseFabricEligibilityFragment) {
			queries.push(preloadFabricEditorEligibility(contentId));
		}

		if (!isLivePageRoute && shouldPreloadPageMetadataBanner) {
			// preload data for whether "unpublished changes" label should show in view page object header
			queries.push(
				preloadWebItemLocation({
					location: 'page.metadata.banner',
					contentId,
				}),
			);
		}
	} else {
		if (
			StatsigConfigurations.isKillSwitchOn('confluence_vp_edit_frag_reader') ||
			!expVal('confluence_exp_vp_document', 'isEnabled', false)
		) {
			queries.push(preloadEditButton({ spaceKey, contentId }));
		}
		queries.push(
			...(shouldUseAnalyticsBylineFragment
				? []
				: [
						slowQueryGuard(
							'preloadContentAnalyticsViewers',
							() => preloadContentAnalyticsViewers(contentId).then(checkHasGraphQLErrors),
							contentHeaderTimeout,
						),
					]),
		);

		if (!isLivePageRoute && shouldPreloadPageMetadataBanner) {
			// preload data for whether "unpublished changes" label should show in view page object header
			queries.push(
				preloadWebItemLocation({
					location: 'page.metadata.banner',
					contentId,
				}),
			);
		}
	}

	return waitForAllReturnWhenError(queries).then((errors) => Boolean(errors || hasErrors));
}
